"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GcmAdapter = void 0;
const socket_io_client_1 = __importDefault(require("socket.io-client"));
const axios_1 = __importDefault(require("axios"));
const uuid_1 = require("uuid");
const helperFunctions_1 = require("./utility/helperFunctions");
const Adapter_1 = require("./Adapter");
const Types_1 = require("./Types");
const ParserService_1 = require("./ParserService");
class GcmAdapter extends Adapter_1.Adapter {
    constructor(options, adapterOptions) {
        super(options);
        this.channels = [];
        this.adapterOptions = (0, helperFunctions_1.defaultTo)(adapterOptions, {});
        if ((0, helperFunctions_1.isNil)(options.productId)) {
            throw new Error(Types_1.BusErrors.MissingProductIdError);
        }
        if ((0, helperFunctions_1.isNil)(options.locale)) {
            throw new Error(Types_1.BusErrors.MissingLocaleError);
        }
        this.setName();
        this.addInitialChannels();
    }
    addInitialChannels() {
        this.addChannel(Types_1.ChannelType.Product, this.options.productId);
        if (this.options.userId && this.options.userType) {
            this.addChannel(Types_1.ChannelType[this.options.userType], this.options.userId);
        }
        if (this.options.initialChannels && this.options.initialChannels.length) {
            this.options.initialChannels.forEach((channel) => {
                this.addChannel(channel.channelType, channel.channelName);
            });
        }
    }
    getClientType() {
        var _a;
        switch ((_a = this.options.applicationName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) {
            default:
                return 'user';
        }
    }
    getClientSubType() {
        var _a;
        switch ((_a = this.options.applicationName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) {
            default:
                return 'Player';
        }
    }
    getSubChannelType(channelType) {
        switch (channelType) {
            case Types_1.ChannelType.Device:
                return 'deviceUuid';
            case Types_1.ChannelType.DeliveryPlatform:
                return 'deliveryPlatform';
            case Types_1.ChannelType.Player:
            default:
                return 'playerUuid';
        }
    }
    getQuery() {
        const clientType = this.getClientType();
        const clientSubType = this.getClientSubType();
        const connectionTime = new Date().toISOString();
        const requestUuid = (0, helperFunctions_1.defaultTo)(this.adapterOptions.requestUuid, (0, uuid_1.v4)());
        const { applicationName, productId, productName, tenantId } = this.options;
        return `clientType=${clientType}
      &clientSubType=${clientSubType}
      &connectionTime=${connectionTime}
      &requestUuid=${requestUuid}
      &deliveryChannel=${applicationName}
      &productName=${productName}
      &UUID=${productId}
      &companyUuid=${tenantId}`
            .replace(/[\n\r]+ */g, '');
    }
    setListeners() {
        if (this.instance) {
            this.instance.on('connect', () => {
                this.subscribeAll();
            });
            this.instance.on('disconnect', (reason) => {
                var _a, _b;
                this.unsubscribeAll();
                if (((_a = this === null || this === void 0 ? void 0 : this.instance) === null || _a === void 0 ? void 0 : _a.disconnected) && reason === 'io server disconnect') {
                    // the disconnection was initiated by the server, need to reconnect manually
                    (_b = this === null || this === void 0 ? void 0 : this.instance) === null || _b === void 0 ? void 0 : _b.connect();
                }
                // otherwise, the socket will automatically try to reconnect after random reconnect delay
            });
        }
    }
    subscribe(channelType, channelName) {
        if (this.instance) {
            if ([Types_1.ChannelType.Product, Types_1.ChannelType.Jackpot, Types_1.ChannelType.Balance].indexOf(channelType) > -1) {
                const subChannels = this.options.subChannels && this.options.subChannels[channelType];
                this.instance.emit('subscribe', {
                    channel: channelName,
                    subChannels: {
                        language: this.options.locale,
                        ...subChannels,
                    },
                });
                this.instance.on(channelName, (payload) => {
                    const data = payload.data || {};
                    (0, ParserService_1.parseAndDispatchEvent)(payload.type, Object.assign(data, { sentTime: payload.sentTime, subscriptionId: channelName }), this.useCommonEventName, this.name, this.options.proxyEvents, this.options.bounceToFrames);
                });
            }
            else {
                this.instance.emit('addSubChannels', {
                    channel: this.options.productId,
                    subChannels: {
                        [this.getSubChannelType(channelType)]: channelName,
                    },
                });
            }
        }
    }
    subscribeAll() {
        this.channels.forEach((channel) => {
            this.subscribe(channel.channelType, channel.channelName);
        });
    }
    unsubscribe(channelType, channelName) {
        if (this.instance) {
            this.instance.off(channelName);
            if (channelType === Types_1.ChannelType.Product) {
                this.instance.emit('unsubscribe', {
                    channel: channelName,
                });
            }
            else {
                this.instance.emit('removeSubChannels', {
                    channel: this.options.productId,
                    subChannels: {
                        [this.getSubChannelType(channelType)]: channelName,
                    },
                });
            }
        }
    }
    unsubscribeAll() {
        this.channels.forEach((channel) => {
            this.unsubscribe(channel.channelType, channel.channelName);
        });
    }
    findChannelIndex(channelType, channelName) {
        return this.channels.findIndex((n) => n.channelType === channelType && n.channelName === channelName);
    }
    setName() {
        this.name = Types_1.Adapters.GcmAdapter;
    }
    getEndpoint() {
        switch (this.options.environment) {
            case 'production':
            // NAR/WAR returns environemnt property in those values
            case 'dc-en':
            case 'dc-de':
            case 'dc-ro':
            case 'dc-ro2':
            case 'dc-rs':
                return `https://gcm-${(0, helperFunctions_1.random)(1, 3)}.de.nsoftcdn.com:8443`;
            default:
                return `https://gcm-fra-staging-${(0, helperFunctions_1.random)(1, 2)}.7platform.com:8008`;
        }
    }
    connect() {
        this.instance = (0, socket_io_client_1.default)(this.getEndpoint(), {
            transports: ['websocket'],
            query: this.getQuery(),
        });
        this.setListeners();
    }
    disconnect() {
        if (this.instance) {
            this.unsubscribeAll();
            this.instance.disconnect();
        }
    }
    reconnect() {
        if (this.instance && this.instance.disconnected) {
            this.instance.connect();
        }
    }
    addChannel(channelType, channelName) {
        if (this.findChannelIndex(channelType, channelName) < 0) {
            this.channels.push({
                channelType,
                channelName,
            });
            this.subscribe(channelType, channelName);
        }
    }
    removeChannel(channelType, channelName) {
        const channelIndex = this.findChannelIndex(channelType, channelName);
        if (channelIndex >= 0) {
            this.channels.splice(channelIndex, 1);
            this.unsubscribe(channelType, channelName);
        }
    }
    sendMessage(message) {
        console.warn(`${this.name}: ${Types_1.BusErrors.MethodNotImplementedError}`, message);
    }
    async getState() {
        const url = `${this.getEndpoint()}/get-state/${this.options.productId}`;
        const response = await axios_1.default.get(url);
        return response.data;
    }
}
exports.GcmAdapter = GcmAdapter;
//# sourceMappingURL=GcmAdapter.js.map